import { Autocomplete, TextField, Chip } from '@mui/material'
import type { FieldMeta } from '@crm/shared'

interface SelectFieldProps {
  field: FieldMeta
  value: unknown
  mode: 'view' | 'edit'
  onChange?: (value: unknown) => void
  error?: string
  disabled?: boolean
  hideLabel?: boolean
  dynamicOptions?: Array<{ value: string; label: string }>
}

function getOptions(field: FieldMeta): Array<{ value: string; label: string }> {
  if (!field.options) return []
  try {
    const parsed = JSON.parse(field.options as unknown as string)
    return parsed.options ?? parsed
  } catch {
    return []
  }
}

export function SelectField({ field, value, mode, onChange, error, disabled, hideLabel, dynamicOptions }: SelectFieldProps) {
  const options = dynamicOptions ?? getOptions(field)

  if (mode === 'view') {
    if (value == null) return <span />
    const opt = options.find((o) => o.value === String(value))
    return <Chip label={opt?.label ?? String(value)} size="small" variant="outlined" />
  }

  const selectedOption = options.find((o) => o.value === String(value)) ?? null

  return (
    <Autocomplete
      size="small"
      disabled={disabled}
      options={options}
      getOptionLabel={(opt) => typeof opt === 'string' ? opt : opt.label}
      value={selectedOption}
      onChange={(_, newVal) => onChange?.(newVal?.value ?? '')}
      isOptionEqualToValue={(opt, val) => opt.value === val.value}
      disableClearable={!!field.is_required}
      renderInput={(params) => (
        <TextField
          {...params}
          label={hideLabel ? undefined : field.label}
          error={!!error}
          helperText={error}
          required={!!field.is_required}
          variant="outlined"
        />
      )}
    />
  )
}
